import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomTabs from '../../components/CustomTabs';
import Header from '../../components/Header';
import ReportingNeeds from '../../components/tables/operations/reporting/ReportingNeeds';
import ReportingReports from '../../components/tables/operations/reporting/ReportingReports';
import ReportingReportsByUser from '../../components/tables/operations/reporting/ReportingReportsByUser';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import { getTomorrowDate, isSameDayOrAfter, isSameDayOrBefore } from '../../utils/dates';
import { localStorageKeys } from '../../utils/enums';
import { getItemFromLocalStorage, storeItemToLocalStorage } from '../../utils/localStorage';
import PeriodReportsTable from '../../components/tables/operations/reporting/PeriodReportsTable';
import { usePageTitle } from '../../hooks/usePageTitle';
import PrimaryBox from '../../components/PrimaryBox';

const ReportingPage: React.FC = () => {
    const [selectedTab, setSelectedTab] = useState(parseInt(getItemFromLocalStorage(localStorageKeys.TAB.REPORTING) || '0'));
    const { t } = useTranslation();
    usePageTitle(capitalizeFirstLetter(t('reporting')));
    const [firstDate, setFirstDate] = useState(new Date(getItemFromLocalStorage(localStorageKeys.REPORTINGSTARTDATE) || new Date()));
    const [secondDate, setSecondDate] = useState(new Date(getItemFromLocalStorage(localStorageKeys.REPORTINGENDDATE) || getTomorrowDate()));

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setSelectedTab(newValue);
        storeItemToLocalStorage(localStorageKeys.TAB.REPORTING, newValue.toString());
    };

    const handleFirstDateChange = (newDate: string) => {
        const tmpDate = new Date(newDate);
        if (!isSameDayOrBefore(tmpDate, secondDate)) return;
        setFirstDate(tmpDate);
        storeItemToLocalStorage(localStorageKeys.REPORTINGSTARTDATE, newDate);
    };

    const handleSecondDateChange = (newDate: string) => {
        const tmpDate = new Date(newDate);
        if (!isSameDayOrAfter(tmpDate, firstDate)) return;
        setSecondDate(tmpDate);
        storeItemToLocalStorage(localStorageKeys.REPORTINGENDDATE, newDate);
    };

    return (
        <PrimaryBox>
            <Header
                title={t('operations')}
                breadcrumbs={[{ label: capitalizeFirstLetter(t('dashboard')) + ' (' + t('beta') + ')' }]}
                subTitle={capitalizeFirstLetter(t('dashboard')) + ' (' + t('beta') + ')'}
                isSecondDateVisible
                initialDate={firstDate}
                onDateChange={(newDate) => handleFirstDateChange(newDate.toISOString())}
                initialSecondDate={secondDate}
                onSecondDateChange={(newDate) => handleSecondDateChange(newDate.toISOString())}
                shortDate
                shortSecondDate
            ></Header>
            <CustomTabs
                selectedTab={selectedTab}
                handleTabChange={handleTabChange}
                tabLabels={[t('reports'), t('report'), t('needs'), t('reports')]}
            />
            {selectedTab === 0 && <ReportingReports startDate={firstDate} endDate={secondDate} />}
            {selectedTab === 1 && <ReportingReportsByUser startDate={firstDate} endDate={secondDate} />}
            {selectedTab === 2 && <ReportingNeeds startDate={firstDate} endDate={secondDate} />}
            {selectedTab === 3 && <PeriodReportsTable startDate={firstDate} endDate={secondDate} />}
        </PrimaryBox>
    );
};

export default ReportingPage;
